import { useTranslation } from "react-i18next";
import { Check, AlertCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";

export function CompletenessRing({ pct = 0, size = 64 }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  const color = pct >= 100 ? "#059669" : pct >= 60 ? "#0891b2" : "#f59e0b";
  return (
    <div className="relative inline-flex items-center justify-center" data-testid="completeness-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <span className="absolute text-sm font-extrabold text-navy">{pct}%</span>
    </div>
  );
}

export function RequirementsChecklist({ reqs, onRequest }) {
  const { t } = useTranslation();
  if (!reqs) return null;
  const docs = reqs.required_documents || [];
  const openKeys = new Set((reqs.open_requests || []).map((r) => r.doc_key));
  const required = docs.filter((d) => d.required);
  const optional = docs.filter((d) => !d.required);
  const Item = ({ d }) => (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid={`req-doc-${d.key}`}>
      <div className="flex items-center gap-2.5">
        {d.present
          ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Check className="h-3.5 w-3.5" /></span>
          : <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-50 text-amber-600"><AlertCircle className="h-3.5 w-3.5" /></span>}
        <div>
          <div className="text-sm font-semibold text-navy">{d.label}</div>
          <div className="text-xs text-slate-400">{d.reason}</div>
        </div>
      </div>
      {d.present
        ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600">{t("p2.present")}</span>
        : openKeys.has(d.key)
          ? <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-bold uppercase text-cyan-700">{t("p2.requested")}</span>
          : onRequest && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onRequest(d.key)} data-testid={`request-doc-${d.key}`}><Send className="mr-1 h-3.5 w-3.5" /> {t("p2.requestDoc")}</Button>}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="requirements-checklist">
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("p2.requiredDocs")}</div>
        {required.map((d) => <Item key={d.key} d={d} />)}
      </div>
      {optional.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("p2.optional")}</div>
          {optional.map((d) => <Item key={d.key} d={d} />)}
        </div>
      )}
    </div>
  );
}
